import { X, Trash2 } from 'lucide-react'
import type { Speed } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { SelectField, SliderField } from '../../common/Field'
import { ColorDot } from '../../common/ColorSwatchPicker'
import { segmentOutputDuration } from '../../../lib/timelineMath'
import { assetDisplayName } from '../../../lib/assetName'

export function SegmentInspector({ segmentId }: { segmentId: string }) {
  const composition = useEditorStore((s) => s.composition)
  const assets = useEditorStore((s) => s.assets)
  const updateSegment = useEditorStore((s) => s.updateSegment)
  const setSegmentSection = useEditorStore((s) => s.setSegmentSection)
  const removeSegment = useEditorStore((s) => s.removeSegment)
  const select = useEditorStore((s) => s.select)
  const isManualOverride = useEditorStore((s) => Boolean(s.manualSectionOverrideIds[segmentId]))

  const segment = composition?.tracks.video.find((s) => s.id === segmentId)
  if (!composition || !segment) return null
  const asset = assets[segment.assetId]
  const section = composition.sections.find((s) => s.id === segment.sectionId)

  return (
    <div className="p-3 space-y-4" data-testid="segment-inspector">
      {/* reviewer minor 2 (UI_MK2_PHASE2.md §3-2): 他の OverlayInspector と同じ
          共通ヘッダー(種類名・×・削除)の見た目に揃える。ここは表示の配置替えのみ
          ── 呼んでいるアクションは元と同じ removeSegment/select(null)、下の
          フィールド群のロジックは無変更。 */}
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">クリップ</div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => removeSegment(segment.id)}
            className="text-text-faint hover:text-accent-red p-1"
            title="削除"
            data-testid="segment-inspector-delete"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={() => select(null)}
            className="text-text-faint hover:text-text p-1"
            title="選択解除"
            data-testid="segment-inspector-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="text-xs text-text-muted truncate">{asset ? assetDisplayName(asset) : '(不明な素材)'}</div>
      <div className="text-[11px] text-text-faint">
        source {segment.inSec.toFixed(2)}s 〜 {segment.outSec.toFixed(2)}s ・出力{' '}
        {segmentOutputDuration(segment).toFixed(2)}s
      </div>

      <SelectField
        label="速度"
        value={String(segment.speed)}
        onChange={(e) => updateSegment(segment.id, { speed: Number(e.target.value) as Speed })}
      >
        <option value="1">等倍 (1x)</option>
        <option value="0.75">スロー (0.75x)</option>
        <option value="0.5">スロー (0.5x)</option>
      </SelectField>

      <SliderField
        label="横位置 (9:16変換時)"
        value={segment.cropX}
        min={0}
        max={1}
        step={0.01}
        onChange={(cropX) => updateSegment(segment.id, { cropX })}
      />

      <SelectField
        label="フィット"
        value={segment.fit}
        onChange={(e) => updateSegment(segment.id, { fit: e.target.value as 'cover' | 'contain' })}
      >
        <option value="cover">カバー (トリミング)</option>
        <option value="contain">全面表示 (レターボックス)</option>
      </SelectField>

      <SelectField
        label="開始トランジション"
        value={segment.transitionIn}
        onChange={(e) => updateSegment(segment.id, { transitionIn: e.target.value as 'cut' | 'fade' })}
      >
        <option value="cut">カット</option>
        <option value="fade">フェード</option>
      </SelectField>

      <div className="flex items-center gap-1.5">
        <div className="flex-1">
          <SelectField
            label="セクション"
            value={segment.sectionId ?? ''}
            onChange={(e) => setSegmentSection(segment.id, e.target.value || null)}
          >
            <option value="">なし</option>
            {composition.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        </div>
        {isManualOverride && (
          <span
            className="mt-4 shrink-0 rounded bg-amber-500/20 text-amber-300 text-[10px] px-1.5 py-0.5 leading-none"
            title="自動編集を再実行するとこの上書きは消えます"
            data-testid="segment-manual-override-badge"
          >
            手動
          </span>
        )}
      </div>
      {section && (
        <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
          <ColorDot color={section.color} /> {section.name}
        </div>
      )}
      {isManualOverride && (
        <p className="text-[10px] text-amber-300/80 -mt-2">
          手動で上書き済み — 自動編集を再実行すると消えます。
        </p>
      )}
    </div>
  )
}
